import { Button, Divider, Form, Segmented, SelectProps } from 'antd';
import {
  useCompaniesReduced,
  useCompanyReduced,
} from '@/core/domain/enterprise/Companies/Companies.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { useEffect, useState } from 'react';
import { useDebounce } from '@/hooks';
import InfiniteSelect from '@/components/_common/InfiniteSelect';
import {
  CompanyReduced,
  ListCompaniesQueryReduced,
} from '@/core/domain/enterprise/Companies/Companies.types';
import useSelectOptions from '@/hooks/useSelectOptions';
import { FaBan } from 'react-icons/fa';

type Props = Omit<SelectProps, 'options'> & {
  queryFilter?: ListCompaniesQueryReduced;
  queryEnable?: boolean;
  selectFirst?: boolean;
  buttonSelectAll?: boolean;
  selectAll?: boolean;
  activityFilter?: boolean;
  onCompanySelected?: (company: CompanyReduced) => void;
};

const CompaniesInfiniteSelectField: React.FC<Props> = ({
  queryFilter,
  queryEnable = false,
  selectFirst = false,
  selectAll = false,
  buttonSelectAll = false,
  activityFilter = false,
  onCompanySelected,
  ...props
}) => {
  const hasFormContext = !!Form.useFormInstance();
  const [selectClicked, setSelectClicked] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>();
  const [companiesActive, setCompaniesActive] = useState<boolean>(true);
  const [changeFilter, setChangeFilter] = useState<boolean>(false);

  const search = useDebounce(searchValue);

  const isMultiple = props.mode === 'multiple';

  const companiesList = useCompaniesReduced(
    {
      active: companiesActive,
      rows: 10,
      name: search,
      isCompanyFilter: true,
      ...queryFilter,
    },
    (selectClicked || queryEnable) && !props.disabled,
  );

  const companies = flatInfiniteData(companiesList.data);

  const companyDetail = useCompanyReduced();

  const companyOptions = useSelectOptions({
    list: companiesList,
    detail: companyDetail,
    selectProps: props,
    labelFormatter: (company) => (
      <>
        {!company.active && queryFilter?.isCompanyFilter === false && (
          <FaBan size={10} style={{ marginRight: 4 }} />
        )}
        {company.name}
      </>
    ),
  });

  useEffect(() => {
    if (
      (companies.length &&
        companies.length === 1 &&
        !props.value &&
        props.onChange &&
        hasFormContext) ||
      (selectFirst && !props.value && props.onChange && hasFormContext)
    ) {
      if (companies[0]) {
        const company = companies[0];
        const propValue = isMultiple ? [company.id] : company.id;

        props.onChange(propValue, {
          value: companies[0]?.id,
          label: companies[0]?.name,
        });

        onCompanySelected?.(company);
      }
    }
  }, [companies, hasFormContext, selectFirst]);

  useEffect(() => {
    if (
      selectAll &&
      !!companies.length &&
      isMultiple &&
      props.onChange &&
      !changeFilter
    ) {
      const activeCompanies = companies.filter(
        (company) => company.active === companiesActive,
      );

      props.onChange(
        activeCompanies.map((company) => company.id),
        activeCompanies.map((company) => ({
          value: company.id,
          label: company.name,
        })),
      );
    }
  }, [
    companies.length,
    hasFormContext,
    selectAll,
    isMultiple,
    changeFilter,
    companiesActive,
  ]);

  return (
    <InfiniteSelect
      fetchNextPage={companiesList.fetchNextPage}
      hasNextPage={companiesList.hasNextPage}
      loading={companiesList.isFetching}
      options={companyOptions}
      showSearch
      searchValue={searchValue}
      onSelect={(companyId: number) => {
        const companySelected = companies.find(({ id }) => id === companyId);

        if (!!companySelected) onCompanySelected?.(companySelected);

        setSelectClicked(false);
      }}
      onDropdownVisibleChange={(open) => setSelectClicked(open)}
      filterOption={false}
      onSearch={setSearchValue}
      maxTagCount={isMultiple ? 'responsive' : undefined}
      maxTagTextLength={isMultiple ? 30 : undefined}
      dropdownRender={(menu) => (
        <>
          {activityFilter && (
            <>
              <Segmented
                block
                className="segmented-boolean"
                value={String(companiesActive)}
                onChange={(value) => {
                  setCompaniesActive(value === 'true');
                  setChangeFilter(true);
                }}
                options={[
                  {
                    value: 'true',
                    label: 'Ativas',
                  },
                  {
                    value: 'false',
                    label: 'Inativas',
                  },
                ]}
              />

              <Divider style={{ margin: '8px 0' }} />
            </>
          )}

          {!!buttonSelectAll && isMultiple && (
            <>
              <Button
                block
                type="link"
                onClick={() => {
                  if (companies && props.onChange) {
                    props.onChange(
                      companies.map((company) => company.id),
                      companies.map((company) => ({
                        value: company.id,
                        label: company.name,
                      })),
                    );
                  }
                }}
              >
                Selecionar Tudo
              </Button>

              <Divider style={{ margin: '8px 0' }} />
            </>
          )}

          {menu}
        </>
      )}
      {...props}
    />
  );
};

export default CompaniesInfiniteSelectField;
